// src/components/ConsentSheet.js
//
// P0-5 (Profil Derin Analiz V2) + RIZA DENETİMİ (12 Haz 2026) — KVKK rıza ekranı.
//
// useConsentGate açtığında gösterilir; kullanıcı zorunlu onay kutularını
// işaretleyip "Devam et"e basana kadar KAPATILAMAZ (KVKK açık rıza
// kaydı zorunlu — overlay tıklaması/ESC ile dismiss yok).
//
// Denetim düzeltmeleri (KVK Kurulu 2018/90 + Çerez Rehberi hizası):
//   - Aydınlatma metni CHECKBOX'TAN ÇIKARILDI → bilgi satırı (aydınlatma
//     onaya bağlanamaz; "okudum + rıza veriyorum" birleşimi hukuka aykırı).
//   - Açık rıza checkbox'ı yalnız rızaya dayanan işlemeye (profil analizi)
//     daraltıldı.
//   - 3. OPSİYONEL checkbox: analitik çerez rızası (varsayılan KAPALI,
//     işaretlenmeden de devam edilir — hizmet şartı DEĞİL). Karar
//     localStorage'a yazılır → CookieConsentBanner bu kullanıcıya hiç
//     görünmez (tek-rıza-yüzeyi kuralı).
//   - Body scroll lock eklendi (arkadaki feed kayıyordu).
//
// NOT: Yasal metinlerin tam halleri yayınlandığında PRIVACY_URL/TERMS_URL
// sabitleri gerçek sayfalara yönlendirilmeli.

import React, { useState } from "react";
import useBodyScrollLock from "../hooks/useBodyScrollLock";
import { writeCookieDecision } from "../myclipsv3/ui/CookieConsentBanner";
import "./ConsentSheet.css";

const PRIVACY_URL = "/gizlilik-politikasi";
const TERMS_URL = "/kullanim-kosullari";

/**
 * Şekil tek üreticiden alındı: `hooks/useConsentGate` → `{ open, busy, error,
 * accept }` (`open`/`busy` boolean state, `error` metin state'i, `accept`
 * argümansız async geri-çağrı). `App.tsx` bu dördünü birebir aktarıyor.
 */
type ConsentSheetProps = {
  open?: boolean;
  busy?: boolean;
  error?: string;
  onAccept?: () => void | Promise<void>;
};

export default function ConsentSheet({ open, busy, error, onAccept }: ConsentSheetProps) {
  // ══ 🚨 HAPİS BİLEREK EKLENMEDİ — ÖLÇÜM ŞARTI KARŞILAMADI ═══════════════
  // (owner kararı bekliyor; kayıt EMİR #98 Dalga-2)
  //
  // Sert şart şuydu: odak hapsi, ÇIKIŞ olmadan konmaz. İlk ölçümüm yetersizdi
  // — buton/link SAYDIM, NE YAPTIKLARINI sormadım (1 buton + 2 link + 3 giriş
  // → "çıkış var" dedim). Şartın onay-benzeri kapılar için sıkı hâli:
  //   ÇIKIŞ, ONAY VERMEYİ GEREKTİRMEMELİ.
  //
  // Bu katmanın gerçek envanteri:
  //   · 3 link — hepsi `target="_blank"` → YENİ SEKME, katmandan çıkış DEĞİL
  //   · 3 onay kutusu → kutu işaretlemek çıkış değil, ONAYIN KENDİSİ
  //   · 1 buton "Devam et" → `onAccept`; zorunlu iki kutu işaretlenmeden
  //     `disabled` (`canSubmit = privacyChecked && termsChecked && !busy`)
  //   · "Reddet" / "Şimdi değil" / "Geri" YOK
  // Yani KABUL ETMEDEN ULAŞILABİLEN ÇIKIŞ YOK.
  //
  // Hapis eklemek ne yapardı: klavye kullanıcısı bugün `Tab` ile arka plana
  // kaçabiliyor (bozuk, ama bir çıkışı var). Hapisle kaçamaz ve tek çıkış
  // ONAY VERMEK olur. Yani hapis, onay tasarımının zorlayıcılığını FİİLEN
  // ETKİNLEŞTİRİR. KVKK'da açık rıza ÖZGÜR İRADEYLE verilmiş olmalı; kullanıcıyı
  // kabul edene kadar tutan bir arayüz o şartı zayıflatır.
  //
  // ⚠️ BU BİR TASARIM TERCİHİNİ DAHA BAĞLAYICI HÂLE GETİRMEK OLURDU VE O
  // TERCİHİ BİZ SEÇMEDİK. "Onay kapısından reddederek çıkılabilir olmalı mı"
  // sorusu ürün + hukuk kararıdır → owner'a gider.
  //
  // Bu yüzden `aria-modal` KARŞILIKSIZ KALIYOR ve bu bir gözden kaçma DEĞİL,
  // kayda geçmiş bir karar: iki kural çarpıştı (vaadin karşılığı olmalı ↔ çıkış
  // olmadan hapis konmaz) ve ikincisi ağır bastı, çünkü hatalı beyan
  // BİLGİLENDİRME kusurudur, kilitlenme ise KULLANICIYI TUTAR.
  //
  // ⚠️ Kancanın capture fazındaki `stopPropagation`ı da bu yüzden İSTENMEZ:
  // ölçüm çıkışı doğrulasaydı sertleştirme olurdu; doğrulamadığı için aynı
  // sorunun ikinci katı olurdu (başka bir işleyicinin Escape'le kapatmasını da
  // engellerdi).
  //
  // YAN GÖZLEM (karar değil, bulgu): zorunlu kutulardan biri "profil önerileri
  // ve derin analiz özellikleri için ... açık rıza" — yani OPSİYONEL özellik
  // rızası hizmete girişin ÖNKOŞULU. KVKK'da açık rızanın hizmet şartına
  // bağlanmaması gerekir. Bunu ÖLÇTÜM, HÜKÜM VERMEDİM; owner'a not.
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);
  const [analyticsChecked, setAnalyticsChecked] = useState(false);
  useBodyScrollLock(!!open);

  if (!open) return null;

  const canSubmit = privacyChecked && termsChecked && !busy;

  const handleSubmit = () => {
    // Çerez kararı (opsiyonel checkbox): işaretli → kabul, değil → ret.
    // Banner bir daha sormaz; karar Ayarlar > Gizlilik'ten değiştirilebilir.
    writeCookieDecision(analyticsChecked ? "accepted" : "rejected");
    onAccept?.();
  };

  return (
    <div className="consent-overlay" role="dialog" aria-modal="true" aria-labelledby="consent-title">
      <div className="consent-card">
        <h2 id="consent-title" className="consent-title">Başlamadan önce</h2>
        <p className="consent-text">
          Mylasa'da gizliliğin sana ait. Tercihler tarih ve sürüm bilgisiyle
          kaydedilir; dilediğinde ayarlardan verilerini indirebilir veya
          hesabını silebilirsin.
        </p>

        {/* Aydınlatma = bilgilendirme; onaya bağlanmaz (KVK Kurulu 2018/90) */}
        <p className="consent-info">
          ⓘ Kişisel verilerin{" "}
          <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">
            Gizlilik Politikası ve KVKK Aydınlatma Metni
          </a>{" "}
          kapsamında işlenir.
        </p>

        <label className="consent-check">
          <input
            type="checkbox"
            checked={termsChecked}
            disabled={busy}
            onChange={(e) => setTermsChecked(e.target.checked)}
          />
          <span>
            <a href={TERMS_URL} target="_blank" rel="noopener noreferrer">
              Kullanım Koşulları
            </a>
            'nı okudum ve kabul ediyorum.
          </span>
        </label>

        <label className="consent-check">
          <input
            type="checkbox"
            checked={privacyChecked}
            disabled={busy}
            onChange={(e) => setPrivacyChecked(e.target.checked)}
          />
          <span>
            Profil önerileri ve derin analiz özellikleri için kişisel
            verilerimin aydınlatma metninde belirtilen kapsamda işlenmesine
            açık rıza veriyorum.
          </span>
        </label>

        <label className="consent-check consent-check--optional">
          <input
            type="checkbox"
            checked={analyticsChecked}
            disabled={busy}
            onChange={(e) => setAnalyticsChecked(e.target.checked)}
          />
          <span>
            <em>(İsteğe bağlı)</em> Deneyimi iyileştirmek için analitik
            çerezlere izin veriyorum. İzin vermesen de Mylasa'yı tüm
            özellikleriyle kullanabilirsin.
          </span>
        </label>

        {error ? (
          <div className="consent-error" role="alert">{error}</div>
        ) : null}

        <button
          type="button"
          className="consent-submit pressable"
          disabled={!canSubmit}
          onClick={handleSubmit}
        >
          {busy ? "Kaydediliyor…" : "Devam et"}
        </button>
        {!canSubmit && !busy ? (
          <p className="consent-hint">Devam etmek için gerekli kutuları işaretle.</p>
        ) : null}
      </div>
    </div>
  );
}
